<template>
  <div>
    <b-card title="Опубликовать рейтинг">
      <b-form-group
          :label="`Презентующий текст (${ratingText.length} симв.)`"
          label-for="rating-text"
      >
        <b-form-textarea
            id="rating-text"
            v-model="ratingText"
            rows="4"
        />
      </b-form-group>

      <div class="d-flex align-items-center mb-1">
        <b-form-checkbox v-model="publishMusic" switch class="mr-2">
          🎵 Музыка
        </b-form-checkbox>
        <b-form-checkbox v-model="publishMovies" switch>
          🎬 Кино
        </b-form-checkbox>
      </div>

      <b-button
          variant="success"
          :disabled="!ratingText || isPublishingRating || (!publishMusic && !publishMovies)"
          @click="publishRating"
      >
        <span v-if="isPublishingRating">
          <b-spinner small />
          Публикация...
        </span>
        <span v-else>
          <feather-icon icon="ImageIcon" class="mr-50" />
          Опубликовать рейтинг
        </span>
      </b-button>
    </b-card>

    <b-card title="Игровые недели">
      <b-overlay :show="isLoading" rounded="sm">
        <b-form-group label="Игровая неделя" label-for="week-select" class="mb-0">
          <v-select
              id="week-select"
              v-model="selectedWeek"
              :options="weeks"
              label="name"
              :reduce="w => w"
              placeholder="Выберите неделю..."
              @input="onWeekChange"
          />
        </b-form-group>
      </b-overlay>
    </b-card>

    <b-card v-if="selectedWeek" title="План / факт">
      <b-overlay :show="isLoadingPlanFact" rounded="sm">
        <b-alert v-if="!isLoadingPlanFact && !planFactError && (!planFact || !(planFact.days || []).length)" show variant="secondary">
          Нет данных о плане/факте — запустите синк или импорт
        </b-alert>

        <template v-else-if="planFact">
          <plan-fact-summary :summary="planFact.summary" />

          <div v-for="day in (planFact.days || [])" :key="day.date">
            <h6 class="mt-1">{{ formatDay(day.date) }}</h6>
            <div class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>План</th>
                    <th>Факт</th>
                    <th>Тема</th>
                    <th>Ведущий</th>
                    <th>Игроков</th>
                    <th>Оплата</th>
                    <th>Статус</th>
                    <th>Задача</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="game in day.games" :key="game.id">
                    <td>{{ formatTime(game.scheduled_at) }}</td>
                    <td :class="{ 'text-warning font-weight-bold': game.is_rescheduled }">
                      {{ game.actual_start_at ? formatTime(game.actual_start_at) : '—' }}
                    </td>
                    <td>{{ game.topic_name || '—' }}</td>
                    <td>
                      {{ game.host_name }}
                      <span v-if="game.is_host_changed" class="text-warning">
                        → {{ game.actual_host_name }}
                      </span>
                    </td>
                    <td>{{ game.players_count != null ? game.players_count : '—' }}</td>
                    <td>{{ formatRub(game.payments_sum) }}</td>
                    <td><plan-fact-status-badge :game="game" /></td>
                    <td>
                      <b-link v-if="game.task_url" :href="game.task_url" target="_blank" rel="noopener noreferrer">
                        задача
                      </b-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>

        <b-alert v-if="planFactError" show variant="danger">
          Не удалось загрузить план/факт недели
        </b-alert>
      </b-overlay>
    </b-card>

    <b-card v-if="selectedWeek" title="Создание постов">
      <b-overlay :show="isLoadingGames" rounded="sm">
        <b-alert v-if="!isLoadingGames && days.length === 0" show variant="warning">
          В этой неделе нет игр
        </b-alert>

        <template v-if="days.length > 0">
          <div
              v-for="day in days"
              :key="day.date"
              class="border rounded p-1 mb-1"
          >
            <b-form-checkbox v-model="day.checked" class="font-weight-bold">
              {{ formatDayLabel(day.date) }}
            </b-form-checkbox>
            <div
                v-for="game in day.games"
                :key="game.id"
                class="ml-2 mt-50 text-muted"
            >
              {{ game.time }} — {{ game.topic.emoji }} {{ game.topic.name }} — {{ game.host }}
            </div>
          </div>

          <b-form-checkbox v-model="createFull" class="mt-1 mb-1">
            Общий анонс на несколько дней
          </b-form-checkbox>

          <b-button
              variant="warning"
              :disabled="checkedDays.length === 0 || isCreatingPosts"
              @click="createPosts"
          >
            <span v-if="isCreatingPosts">
              <b-spinner small />
              Создание...
            </span>
            <span v-else>
              <feather-icon icon="FileTextIcon" class="mr-50" />
              Создать посты
            </span>
          </b-button>
        </template>

        <div class="mt-1">
          <b-button
              variant="info"
              :disabled="isLoadingPackPosts"
              @click="loadPackPosts"
          >
            <span v-if="isLoadingPackPosts">
              <b-spinner small />
              Генерация...
            </span>
            <span v-else>
              <feather-icon icon="ImageIcon" class="mr-50" />
              Описания пакетов
            </span>
          </b-button>
        </div>

        <template v-if="packPosts">
          <div
              v-for="post in packPosts"
              :key="post.pack_key"
              class="border rounded p-1 mt-1"
          >
            <div class="d-flex justify-content-between align-items-start mb-50">
              <div class="font-weight-bold">
                {{ post.topic.emoji }} {{ post.topic.name }} — {{ post.send_at_label }}
              </div>
              <b-button
                  v-if="!isPackPostsPublished"
                  size="sm"
                  variant="outline-danger"
                  @click="removePackPost(post)"
              >
                <feather-icon icon="Trash2Icon" class="mr-25" />
                Убрать
              </b-button>
            </div>
            <b-row>
              <b-col md="4">
                <b-alert v-if="isPackPostsPublished" show variant="success" class="p-50 mb-50">
                  ✅ Пост отложен
                </b-alert>

                <b-form-radio-group
                    v-if="!isPackPostsPublished"
                    v-model="post.media_mode"
                    :options="[{ text: '🎬 Видео', value: 'video' }, { text: '🖼 Обложка', value: 'image' }]"
                    buttons
                    button-variant="outline-secondary"
                    size="sm"
                    class="mb-50"
                    @change="onMediaModeChange(post)"
                />

                <template v-if="!isPackPostsPublished && post.media_mode === 'image'">
                  <b-overlay :show="post.isCoverLoading" rounded="sm">
                    <b-img v-if="post.image_url" :src="post.image_url" fluid rounded alt="Обложка" />
                    <div v-else class="text-muted p-2">Обложка генерируется...</div>
                  </b-overlay>
                  <b-button
                      size="sm"
                      variant="outline-secondary"
                      class="mt-50"
                      :disabled="post.isCoverLoading"
                      @click="generateCover(post)"
                  >
                    🔄 Другой фон
                  </b-button>
                </template>

                <template v-else-if="!isPackPostsPublished">
                  <b-overlay :show="post.isVideoUploading" rounded="sm">
                    <video
                        v-if="post.video_url"
                        :src="post.video_url"
                        controls
                        class="pack-post-video rounded"
                    />
                    <div v-else class="text-muted p-2">Видео не загружено</div>
                  </b-overlay>
                  <b-form-file
                      accept=".mp4,.mov"
                      class="mt-50"
                      placeholder="Выбрать видео (mp4/mov)..."
                      browse-text="Обзор"
                      :disabled="post.isVideoUploading"
                      @input="file => uploadPackVideo(post, file)"
                  />
                </template>

                <hr>

                <div class="font-weight-bold mb-50">📖 Сторис</div>
                <b-overlay :show="post.isStoryUploading" rounded="sm">
                  <video
                      v-if="post.story_video_url"
                      :src="post.story_video_url"
                      controls
                      class="pack-post-video rounded"
                  />
                </b-overlay>
                <b-form-file
                    accept=".mp4,.mov"
                    class="mt-50"
                    placeholder="Видео для сторис (mp4/mov)..."
                    browse-text="Обзор"
                    :disabled="post.isStoryUploading"
                    @input="file => uploadStoryVideo(post, file)"
                />
                <b-button
                    v-if="post.story_video_path"
                    size="sm"
                    variant="outline-info"
                    class="mt-50"
                    :disabled="post.isStoryPublishing"
                    @click="publishPackStory(post)"
                >
                  <span v-if="post.isStoryPublishing">
                    <b-spinner small />
                    Публикация...
                  </span>
                  <span v-else>📖 Опубликовать в сторис</span>
                </b-button>
              </b-col>
              <b-col md="8">
                <div class="pack-post-text">{{ post.text }}</div>
              </b-col>
            </b-row>
          </div>

          <b-button
              v-if="!isPackPostsPublished"
              variant="success"
              class="mt-1"
              :disabled="!allMediaReady || isPublishingPackPosts"
              @click="publishPackPosts"
          >
            <span v-if="isPublishingPackPosts">
              <b-spinner small />
              Отправка...
            </span>
            <span v-else>
              <feather-icon icon="SendIcon" class="mr-50" />
              Отправить в отложенные
            </span>
          </b-button>
        </template>
      </b-overlay>
    </b-card>

    <b-card v-if="selectedWeek" title="Подвести итоги">
      <b-overlay :show="isLoadingSummary" rounded="sm">
        <b-button
            variant="primary"
            :disabled="isLoadingSummary"
            @click="loadSummary"
        >
          <feather-icon icon="AwardIcon" class="mr-50" />
          Сформировать итоги
        </b-button>

        <template v-if="summaryText !== null">
          <b-form-group
              :label="`Текст поста (${summaryText.length} симв.)`"
              label-for="summary-text"
              class="mt-2"
          >
            <b-form-textarea
                id="summary-text"
                v-model="summaryText"
                rows="20"
            />
          </b-form-group>

          <b-button
              variant="success"
              :disabled="!summaryText || isPublishing"
              @click="publishSummary"
          >
            <span v-if="isPublishing">
              <b-spinner small />
              Публикация...
            </span>
            <span v-else>
              <feather-icon icon="SendIcon" class="mr-50" />
              Опубликовать в канал
            </span>
          </b-button>
        </template>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BOverlay,
  BAlert,
  BSpinner,
  BRow,
  BCol,
  BImg,
  BFormFile,
  BFormRadioGroup,
  BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onUnmounted, ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import gameStoreModule from '@/views/game/gameStoreModule'
import playersStoreModule from '@/views/players/playersStoreModule'
import { renderRatingBlob } from '@/views/players/exportRatingImage'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PlanFactStatusBadge from '@/views/game/components/PlanFactStatusBadge.vue'
import PlanFactSummary from '@/views/game/components/PlanFactSummary.vue'
import { formatDay, formatTime } from '@/views/game/planFact'
import { formatRub } from '@/views/finance/utils'

export default {
  components: {
    BCard,
    BFormGroup,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BOverlay,
    BAlert,
    BSpinner,
    BRow,
    BCol,
    BImg,
    BFormFile,
    BFormRadioGroup,
    BLink,
    vSelect,
    PlanFactStatusBadge,
    PlanFactSummary,
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'app-games'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const PLAYERS_STORE_MODULE_NAME = 'app-players'
    if (!store.hasModule(PLAYERS_STORE_MODULE_NAME)) {
      store.registerModule(PLAYERS_STORE_MODULE_NAME, playersStoreModule)
    }
    onUnmounted(() => {
      if (store.hasModule(PLAYERS_STORE_MODULE_NAME)) store.unregisterModule(PLAYERS_STORE_MODULE_NAME)
    })

    // Строка меток собирается из включённых кластеров
    const buildRatingText = (music, movies) => {
      const labels = []
      if (music) labels.push('🎵 Музыка')
      if (movies) labels.push('🎬 Кино')

      // Ссылка голым URL: подпись одиночного фото уходит в TDLib plain-текстом
      // (без HTML-парсинга), Telegram сам делает URL кликабельным
      return `🏆 Рейтинг игроков «Своя Игра ФНФ» за последние 90 дней\n\n${labels.join('   ')}`
        + '\n\nℹ️ Как считается рейтинг: https://t.me/svoyaigrafnf/15045'
    }

    const toast = useToast()

    const isLoading = ref(false)
    const isLoadingGames = ref(false)
    const isCreatingPosts = ref(false)
    const isLoadingSummary = ref(false)
    const isPublishing = ref(false)
    const isPublishingRating = ref(false)
    const publishMusic = ref(true)
    const publishMovies = ref(true)
    // Последний авто-сгенерированный текст — чтобы отличить ручную правку от дефолта
    const lastAutoRatingText = ref(buildRatingText(true, true))
    const ratingText = ref(lastAutoRatingText.value)

    // Пока текст не правили вручную (равен последнему авто-шаблону) — пересобираем при смене кластеров
    watch([publishMusic, publishMovies], ([music, movies]) => {
      const nextText = buildRatingText(music, movies)
      if (ratingText.value === lastAutoRatingText.value) {
        ratingText.value = nextText
      }
      lastAutoRatingText.value = nextText
    })
    const weeks = ref([])
    const selectedWeek = ref(null)
    const days = ref([])
    const planFact = ref(null)
    const isLoadingPlanFact = ref(false)
    const planFactError = ref(false)
    const createFull = ref(true)
    const summaryText = ref(null)
    const packPosts = ref(null) // null — блок скрыт; [] не бывает (бэкенд кидает 422)
    const isLoadingPackPosts = ref(false)
    const isPublishingPackPosts = ref(false)
    const isPackPostsPublished = ref(false)

    const checkedDays = computed(() => days.value.filter(d => d.checked).map(d => d.date))
    // Готовность медиа каждого поста: в режиме обложки — сгенерированная
    // картинка, в режиме видео — загруженный файл
    const allMediaReady = computed(() => Boolean(packPosts.value)
        && packPosts.value.every(post => (post.media_mode === 'image'
          ? (post.image_path && !post.isCoverLoading)
          : (post.video_path && !post.isVideoUploading))))

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckCircleIcon',
          variant,
        },
      })
    }

    const errorMessage = (error, fallback) => (
      (error.response && error.response.data && error.response.data.message) || fallback
    )

    // Сегодняшняя дата в Москве в формате YYYY-MM-DD
    const moscowToday = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Moscow' }).format(new Date())

    // Дефолт: отмечены дни от сегодня; если сегодня воскресенье — сегодняшний день снят
    // (повторяет прежнюю серверную логику отбора игр для постов)
    const isDayCheckedByDefault = date => {
      const today = moscowToday()
      if (date < today) return false
      if (date === today && new Date(`${date}T00:00:00`).getDay() === 0) return false
      return true
    }

    const formatDayLabel = date => new Date(`${date}T00:00:00`)
        .toLocaleDateString('ru-RU', {
          weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
        })

    isLoading.value = true
    store.dispatch('app-games/fetchGameWeeks')
        .then(response => {
          weeks.value = response.data.data
        })
        .catch(() => showToast('Ошибка загрузки недель', 'danger'))
        .finally(() => {
          isLoading.value = false
        })

    const onWeekChange = () => {
      packPosts.value = null
      isPackPostsPublished.value = false
      days.value = []
      summaryText.value = null
      planFact.value = null
      if (!selectedWeek.value) return

      isLoadingGames.value = true
      store.dispatch('app-games/fetchWeekGames', { week_id: selectedWeek.value.id })
          .then(response => {
            days.value = response.data.data.map(day => ({
              ...day,
              checked: isDayCheckedByDefault(day.date),
            }))
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка загрузки игр недели'), 'danger'))
          .finally(() => {
            isLoadingGames.value = false
          })

      isLoadingPlanFact.value = true
      planFactError.value = false
      store.dispatch('app-games/fetchWeekPlanFact', { week_id: selectedWeek.value.id })
          .then(response => {
            planFact.value = response.data.data
          })
          .catch(error => {
            planFactError.value = true
            showToast(errorMessage(error, 'Ошибка загрузки плана/факта'), 'danger')
          })
          .finally(() => {
            isLoadingPlanFact.value = false
          })
    }

    const createPosts = () => {
      isCreatingPosts.value = true
      store.dispatch('app-games/createWeekPosts', {
        week_id: selectedWeek.value.id,
        days: checkedDays.value,
        create_full: createFull.value,
      })
          .then(response => showToast(response.data.message || 'Посты успешно созданы', 'success'))
          .catch(error => showToast(errorMessage(error, 'Ошибка при создании постов'), 'danger'))
          .finally(() => {
            isCreatingPosts.value = false
          })
    }

    const generateCover = post => {
      // Vue 2: поля добавлены заранее в loadPackPosts, реактивность сохраняется
      post.isCoverLoading = true
      return store.dispatch('app-games/generatePackCover', {
        week_id: selectedWeek.value.id,
        pack_key: post.pack_key,
      })
          .then(response => {
            post.image_path = response.data.data.image_path
            post.image_url = response.data.data.image_url
            if (response.data.data.warning) showToast(response.data.data.warning, 'danger')
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка генерации обложки'), 'danger'))
          .finally(() => {
            post.isCoverLoading = false
          })
    }

    const loadPackPosts = () => {
      isLoadingPackPosts.value = true
      packPosts.value = null
      isPackPostsPublished.value = false
      store.dispatch('app-games/fetchPackPostsPreview', { week_id: selectedWeek.value.id })
          .then(response => {
            // Поля обложки объявляем сразу — иначе Vue 2 не увидит их появление
            // По умолчанию режим видео; обложка генерируется лениво —
            // при первом переключении на вкладку «Обложка»
            packPosts.value = response.data.data.map(post => ({
              ...post,
              media_mode: 'video',
              image_path: null,
              image_url: null,
              isCoverLoading: false,
              video_path: null,
              video_url: null,
              isVideoUploading: false,
              // Сторис независима от медиа поста: своё видео и свои флаги
              story_video_path: null,
              story_video_url: null,
              isStoryUploading: false,
              isStoryPublishing: false,
            }))
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка формирования описаний пакетов'), 'danger'))
          .finally(() => {
            isLoadingPackPosts.value = false
          })
    }

    const removePackPost = post => {
      // Повторы пакетов анонсировать не нужно — убираем пост из предпросмотра.
      // Время отправки остальных пересчитает бэкенд при публикации
      packPosts.value = packPosts.value.filter(item => item.pack_key !== post.pack_key)
      if (!packPosts.value.length) {
        // Пустой список не храним — блок скрывается (см. объявление packPosts)
        packPosts.value = null
      }
    }

    const onMediaModeChange = post => {
      if (post.media_mode === 'image' && !post.image_path && !post.isCoverLoading) {
        generateCover(post)
      }
    }

    const publishPackStory = post => {
      root.$swal({
        title: 'Опубликовать сторис?',
        text: 'Сторис публикуется в канале сразу, без отложки',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Опубликовать',
        cancelButtonText: 'Отмена',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (!result.value) return

        post.isStoryPublishing = true
        store.dispatch('app-games/publishPackStory', {
          week_id: selectedWeek.value.id,
          pack_key: post.pack_key,
          video_path: post.story_video_path,
          text: post.text,
        })
            .then(response => showToast(response.data.message || 'Сторис опубликована', 'success'))
            .catch(error => showToast(errorMessage(error, 'Ошибка публикации сторис'), 'danger'))
            .finally(() => {
              post.isStoryPublishing = false
            })
      })
    }

    const uploadPackVideo = (post, file) => {
      if (!file) return
      post.isVideoUploading = true
      store.dispatch('app-games/uploadPackVideo', {
        week_id: selectedWeek.value.id,
        pack_key: post.pack_key,
        file,
      })
          .then(response => {
            post.video_path = response.data.data.video_path
            post.video_url = response.data.data.video_url
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка загрузки видео'), 'danger'))
          .finally(() => {
            post.isVideoUploading = false
          })
    }

    const uploadStoryVideo = (post, file) => {
      if (!file) return
      post.isStoryUploading = true
      store.dispatch('app-games/uploadPackVideo', {
        week_id: selectedWeek.value.id,
        pack_key: post.pack_key,
        file,
      })
          .then(response => {
            post.story_video_path = response.data.data.video_path
            post.story_video_url = response.data.data.video_url
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка загрузки видео для сторис'), 'danger'))
          .finally(() => {
            post.isStoryUploading = false
          })
    }

    const publishPackPosts = () => {
      root.$swal({
        title: 'Отправить посты в отложенные?',
        text: `Постов: ${packPosts.value.length}. Первый — ${packPosts.value[0].send_at_label}, далее +10 минут`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Отправить',
        cancelButtonText: 'Отмена',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (!result.value) return

        isPublishingPackPosts.value = true
        store.dispatch('app-games/publishPackPosts', {
          week_id: selectedWeek.value.id,
          // Ненужный медиа-ключ не отправляем вовсе: бэкенд требует ровно
          // одно из image_path/video_path
          posts: packPosts.value.map(post => {
            const payload = { pack_key: post.pack_key, text: post.text }
            if (post.media_mode === 'video') {
              payload.video_path = post.video_path
            } else {
              payload.image_path = post.image_path
            }
            return payload
          }),
        })
            .then(response => {
              showToast(response.data.message || 'Посты отправлены в отложенные', 'success')
              // Блок не очищаем: сторис — независимое действие, её можно
              // отправить и после публикации постов
              isPackPostsPublished.value = true
            })
            .catch(error => showToast(errorMessage(error, 'Ошибка отправки отложенных постов'), 'danger'))
            .finally(() => {
              isPublishingPackPosts.value = false
            })
      })
    }

    const loadSummary = () => {
      isLoadingSummary.value = true
      store.dispatch('app-games/fetchWeekSummary', { week_id: selectedWeek.value.id })
          .then(response => {
            summaryText.value = response.data.data.text
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка формирования итогов'), 'danger'))
          .finally(() => {
            isLoadingSummary.value = false
          })
    }

    const publishSummary = () => {
      root.$swal({
        title: 'Опубликовать пост в канале?',
        text: 'Пост будет сразу опубликован в ТГ-канале',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Опубликовать',
        cancelButtonText: 'Отмена',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (!result.value) return

        isPublishing.value = true
        store.dispatch('app-games/publishWeekSummary', {
          week_id: selectedWeek.value.id,
          text: summaryText.value,
        })
            .then(() => showToast('Пост опубликован в канале', 'success'))
            .catch(error => showToast(errorMessage(error, 'Ошибка публикации поста'), 'danger'))
            .finally(() => {
              isPublishing.value = false
            })
      })
    }

    const publishRating = () => {
      const clusters = []
      if (publishMusic.value) clusters.push('music')
      if (publishMovies.value) clusters.push('movies')

      const dialogText = clusters.length === 2
        ? 'Альбом из 2 картинок (Музыка и Кино) будет опубликован в ТГ-канале'
        : `Будет опубликована 1 картинка (${publishMusic.value ? 'Музыка' : 'Кино'}) в ТГ-канале`

      root.$swal({
        title: 'Опубликовать рейтинг в канале?',
        text: dialogText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Опубликовать',
        cancelButtonText: 'Отмена',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.value) return

        isPublishingRating.value = true
        try {
          const responses = await Promise.all(
            clusters.map(cluster => store.dispatch('app-players/fetchRating', { cluster })),
          )

          const playersByCluster = {}
          clusters.forEach((cluster, index) => {
            playersByCluster[cluster] = (responses[index].data && responses[index].data.players) || []
          })

          if (clusters.some(cluster => !playersByCluster[cluster].length)) {
            showToast('Недостаточно данных рейтинга для публикации', 'danger')
            return
          }

          const blobs = await Promise.all(
            clusters.map(cluster => renderRatingBlob({ players: playersByCluster[cluster], cluster })),
          )

          const blobByCluster = {}
          clusters.forEach((cluster, index) => {
            blobByCluster[cluster] = blobs[index]
          })

          await store.dispatch('app-games/publishRating', {
            text: ratingText.value,
            musicBlob: blobByCluster.music || null,
            moviesBlob: blobByCluster.movies || null,
          })

          showToast('Рейтинг опубликован в канале', 'success')
        } catch (error) {
          showToast(errorMessage(error, 'Ошибка публикации рейтинга'), 'danger')
        } finally {
          isPublishingRating.value = false
        }
      })
    }

    return {
      isLoading,
      isLoadingGames,
      isCreatingPosts,
      isLoadingSummary,
      isPublishing,
      weeks,
      selectedWeek,
      days,
      createFull,
      summaryText,
      checkedDays,
      planFact,
      isLoadingPlanFact,
      planFactError,
      formatDay,
      formatTime,
      formatRub,
      onWeekChange,
      createPosts,
      loadSummary,
      publishSummary,
      isPublishingRating,
      ratingText,
      publishMusic,
      publishMovies,
      publishRating,
      formatDayLabel,
      packPosts,
      isLoadingPackPosts,
      isPublishingPackPosts,
      allMediaReady,
      isPackPostsPublished,
      loadPackPosts,
      generateCover,
      removePackPost,
      onMediaModeChange,
      uploadPackVideo,
      uploadStoryVideo,
      publishPackStory,
      publishPackPosts,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.pack-post-text {
  white-space: pre-wrap;
}

.pack-post-video {
  max-width: 100%;
}
</style>
